import { useState, useEffect, useCallback } from 'react';
import { Loader2, Github, Server, CreditCard, CheckCircle, X, Check, Database, GitBranch, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getGithubRepos } from '@/api/auth';
import { createProjectCheckoutSession } from '@/api/projects';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isFirstProject?: boolean;
}

interface GithubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description?: string;
}

interface OdooVersion {
  version: string;
  name: string;
  is_latest?: boolean;
}

// Server data types from the backend
interface ServerPlanInfo {
  name: string;
  vcpus?: number;
  ram?: number;
  disk?: number;
  description?: string;
  type?: string;
  base_price?: number;
  capacity?: {
    visitors: string;
    workers: string;
  };
  region_specs?: Record<string, {
    vcpus?: number;
    ram?: number;
    disk?: number;
    description?: string;
  }>;
}

interface ServerData {
  success: boolean;
  starter_plans: Record<string, ServerPlanInfo>;
  shared_plans: Record<string, ServerPlanInfo>;
  dedicated_plans: Record<string, ServerPlanInfo>;
  regions: Record<string, { name: string; location: string; flag: string }>;
  pricing: Record<string, Record<string, number>>;
  cpu_types: Record<string, { name: string; description: string }>;
}

// Static plan details with user/worker info (same as Flask)
const STATIC_PLAN_DETAILS: Record<string, { 
  users: string; 
  workers: string; 
  badges: string[]; 
  best_for: string;
  cpu_type: string;
  // Region-specific specs for US regions (hil, ash)
  us_specs?: { cpu: number; ram: number; disk: number };
}> = {
  // Shared CPU plans
  cpx22: { users: '2-5 users', workers: '~2 workers', badges: ['Premium AMD CPU'], best_for: 'Small teams, testing, development environments', cpu_type: 'shared', us_specs: { cpu: 2, ram: 2, disk: 40 } },
  cpx32: { users: '5-15 users', workers: '~4 workers', badges: ['Premium AMD CPU'], best_for: 'Growing teams, production-ready for small businesses', cpu_type: 'shared', us_specs: { cpu: 3, ram: 4, disk: 80 } },
  cpx42: { users: '15-30 users', workers: '~8 workers', badges: ['Premium AMD CPU'], best_for: 'Mid-size businesses, multi-module deployments', cpu_type: 'shared', us_specs: { cpu: 4, ram: 8, disk: 160 } },
  cpx52: { users: '30-50 users', workers: '~12 workers', badges: ['Premium AMD CPU'], best_for: 'Larger teams, heavy reporting and integrations', cpu_type: 'shared', us_specs: { cpu: 8, ram: 16, disk: 240 } },
  cpx62: { users: '50-100 users', workers: '~15 workers', badges: ['Premium AMD CPU'], best_for: 'Large teams, full ERP suite, e-commerce integration', cpu_type: 'shared', us_specs: { cpu: 16, ram: 32, disk: 360 } },
  // Dedicated CPU plans
  ccx13: { users: '5-15 users', workers: '~3 workers', badges: ['Dedicated CPU', 'Premium AMD CPU'], best_for: 'Production-ready for small teams, guaranteed performance', cpu_type: 'dedicated' },
  ccx23: { users: '15-35 users', workers: '~7 workers', badges: ['Dedicated CPU', 'Premium AMD CPU'], best_for: 'Mid-size businesses, consistent performance, multi-app usage', cpu_type: 'dedicated' },
  ccx33: { users: '35-70 users', workers: '~15 workers', badges: ['Dedicated CPU', 'Premium AMD CPU'], best_for: 'Large businesses, heavy workloads, consistent SLAs', cpu_type: 'dedicated' },
  ccx43: { users: '70-150 users', workers: '~31 workers', badges: ['Dedicated CPU', 'Premium AMD CPU'], best_for: 'Enterprise deployments, mission-critical applications', cpu_type: 'dedicated' },
};

const ODOO_VERSIONS: OdooVersion[] = [
  { version: '19.0', name: '19.0 (Latest)', is_latest: true },
  { version: '18.0', name: '18.0' },
  { version: '17.0', name: '17.0' },
  { version: '16.0', name: '16.0' },
];

// Regions from server_plans.py
const REGIONS = [
  { id: 'nbg1', name: 'Nuremberg', location: 'Germany', flag: '🇩🇪' },
  { id: 'fsn1', name: 'Falkenstein', location: 'Germany', flag: '🇩🇪' },
  { id: 'hel1', name: 'Helsinki', location: 'Finland', flag: '🇫🇮' },
  { id: 'sin', name: 'Singapore', location: 'Singapore', flag: '🇸🇬' },
  { id: 'hil', name: 'Hillsboro', location: 'Oregon, USA', flag: '🇺🇸' },
  { id: 'ash', name: 'Ashburn', location: 'Virginia, USA', flag: '🇺🇸' },
];

export function CreateProjectDialog({ open, onOpenChange, isFirstProject }: CreateProjectDialogProps) {

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [projectName, setProjectName] = useState('');
  const [odooVersion, setOdooVersion] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [odooEnterpriseCode, setOdooEnterpriseCode] = useState('');
  
  // Step 2: Plan selection
  const [selectedPlan, setSelectedPlan] = useState<'pro_plan' | 'starter_plan'>('pro_plan');
  
  // Step 3: Server configuration
  const [serverChoice, setServerChoice] = useState<'managed' | 'byos'>('managed');
  const [cpuType, setCpuType] = useState<'shared' | 'dedicated'>('shared');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedServerPlan, setSelectedServerPlan] = useState('');
  
  // Step 4: Add-ons
  const [developerBranches, setDeveloperBranches] = useState(true);

  // Data state
  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validatingEnterprise, setValidatingEnterprise] = useState(false);
  const [enterpriseCodeValid, setEnterpriseCodeValid] = useState<boolean | null>(null);
  const [enterpriseValidationError, setEnterpriseValidationError] = useState<string | null>(null);
  
  // Server data from API
  const [serverData, setServerData] = useState<ServerData | null>(null);
  const [loadingServerData, setLoadingServerData] = useState(false);
  
  // Server availability tracking
  const [planAvailability, setPlanAvailability] = useState<Record<string, boolean | null>>({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const totalSteps = 5;

  useEffect(() => {
    if (open) {
      if (repos.length === 0) {
        loadGithubRepos();
      }
      if (!serverData) {
        loadServerData();
      }
    }
  }, [open]);

  // Clear server plan when region or cpu type changes
  useEffect(() => {
    setSelectedServerPlan('');
    setPlanAvailability({});
  }, [selectedRegion, cpuType]);

  // Reset server selections when plan type changes
  useEffect(() => {
    setSelectedRegion('');
    setSelectedServerPlan('');
    setPlanAvailability({});
    // Reset to default server choice for starter plan
    if (selectedPlan === 'starter_plan') {
      setServerChoice('managed');
    }
  }, [selectedPlan]);

  const loadGithubRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await getGithubRepos();
      if (response.success) {
        setRepos(response.repos || []);
      } else {
        console.error('Failed to load GitHub repos:', response.error);
        setRepos([]);
      }
    } catch (error) {
      console.error('Failed to load GitHub repos:', error);
      setRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadServerData = async () => {
    setLoadingServerData(true);
    try {
      const response = await fetch('/api/project/server-data');
      const data = await response.json();
      if (data.success) {
        setServerData(data);
      }
    } catch (error) {
      console.error('Failed to load server data:', error);
    } finally {
      setLoadingServerData(false);
    }
  };

  const checkAvailability = useCallback(async () => {
    if (!selectedRegion) return;
    
    setCheckingAvailability(true);
    const plans = getAvailablePlans();
    const newAvailability: Record<string, boolean | null> = {};
    
    plans.forEach(plan => {
      newAvailability[plan.id] = null;
    });
    setPlanAvailability(newAvailability);
    
    await Promise.all(plans.map(async (plan) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(
          `/api/project/check-server-availability?server_type=${plan.id}&location=${selectedRegion}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        
        const data = await response.json();
        setPlanAvailability(prev => ({ ...prev, [plan.id]: data.available }));
      } catch (error) {
        setPlanAvailability(prev => ({ ...prev, [plan.id]: true }));
      }
    }));
    
    setCheckingAvailability(false);
  }, [selectedRegion, selectedPlan, cpuType]);

  // Check availability when region changes - ONLY on step 3
  useEffect(() => {
    if (currentStep === 3 && selectedRegion && (selectedPlan === 'starter_plan' || serverChoice === 'managed')) {
      checkAvailability();
    }
  }, [selectedRegion, cpuType, currentStep, selectedPlan, serverChoice, checkAvailability]);

  const validateEnterpriseCode = async () => {
    if (!odooEnterpriseCode.trim()) return;

    setValidatingEnterprise(true);
    setEnterpriseValidationError(null);

    try {
      const response = await fetch('/api/project/validate-enterprise-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: odooEnterpriseCode.trim() }),
      });

      const result = await response.json();

      if (result.valid) {
        setEnterpriseCodeValid(true);
      } else {
        setEnterpriseCodeValid(false);
        setEnterpriseValidationError(result.error || 'Invalid subscription code');
      }
    } catch (error) {
      setEnterpriseCodeValid(false);
      setEnterpriseValidationError('Failed to validate code. Please try again.');
    } finally {
      setValidatingEnterprise(false);
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep === totalSteps) {
        handleCreateProject();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateCurrentStep = (): boolean => {
    setError(null);

    switch (currentStep) {
      case 1:
        if (!projectName.trim()) {
          setError('Project name is required');
          return false;
        }
        if (!odooVersion) {
          setError('Please select an Odoo version');
          return false;
        }
        if (!selectedRepo) {
          setError('Please select a GitHub repository');
          return false;
        }
        // Enterprise code validation: if provided, must be validated and valid
        if (odooEnterpriseCode.trim() && enterpriseCodeValid === false) {
          setError('Please validate your Odoo Enterprise code before proceeding');
          return false;
        }
        break;
      case 2:
        // Plan selection - always valid as we have a default
        break;
      case 3:
        // Server configuration
        if (selectedPlan === 'starter_plan' || (selectedPlan === 'pro_plan' && serverChoice === 'managed')) {
          if (!selectedRegion) {
            setError('Please select a region');
            return false;
          }
          if (!selectedServerPlan) {
            setError('Please select a server plan');
            return false;
          }
          // Check if selected plan is available
          if (planAvailability[selectedServerPlan] === false) {
            setError('Selected server plan is not available in this region');
            return false;
          }
        }
        break;
      case 4:
        // Add-ons - always valid
        break;
      case 5:
        // Review step - always valid
        break;
    }

    return true;
  };

  const handleCreateProject = async () => {
    setCreating(true);
    setError(null);

    try {
      const response = await createProjectCheckoutSession({
        name: projectName,
        odoo_version: odooVersion,
        github_repo: selectedRepo,
        plan: selectedPlan,
        server_choice: serverChoice,
        cpu_type: cpuType,
        region: selectedRegion,
        server_plan: selectedServerPlan,
        developer_branches: developerBranches,
        backup_storage: true, // Always included
        odoo_enterprise_code: odooEnterpriseCode.trim() || undefined,
      });

      if (response.success && response.url) {
        // Redirect to Stripe checkout
        window.location.href = response.url;
      } else {
        setError(response.error || 'Failed to create checkout session');
        setCreating(false);
      }
    } catch (error) {
      setError('Failed to create project. Please try again.');
      setCreating(false);
    }
  };

  // Get available server plans based on plan type and CPU type
  const getAvailablePlans = useCallback(() => {
    if (!serverData) return [];
    
    if (selectedPlan === 'starter_plan') {
      return Object.entries(serverData.starter_plans || {}).map(([id, plan]) => ({
        id,
        ...plan,
        ...getStaticPlanDetails(id),
      }));
    }
    
    const plans = cpuType === 'shared' ? serverData.shared_plans : serverData.dedicated_plans;
    return Object.entries(plans || {}).map(([id, plan]) => ({
      id,
      ...plan,
      ...getStaticPlanDetails(id),
    }));
  }, [serverData, selectedPlan, cpuType]);

  // Get static plan details (users, workers, badges)
  const getStaticPlanDetails = (planId: string) => {
    return STATIC_PLAN_DETAILS[planId] || { users: '', workers: '', badges: [], best_for: '', cpu_type: 'shared' };
  };

  // Get plan specs (adjusts for US regions)
  const getPlanSpecs = (planId: string, plan: ServerPlanInfo) => {
    const isUSRegion = selectedRegion === 'hil' || selectedRegion === 'ash';
    
    // For starter plans, check region_specs first
    if (plan.region_specs && selectedRegion && plan.region_specs[selectedRegion]) {
      const regionSpec = plan.region_specs[selectedRegion];
      return {
        vcpus: regionSpec.vcpus || plan.vcpus,
        ram: regionSpec.ram || plan.ram,
        disk: regionSpec.disk || plan.disk,
        description: regionSpec.description || plan.description,
      };
    }
    
    // For shared CPU plans in US regions, use reduced specs
    const staticPlan = STATIC_PLAN_DETAILS[planId];
    if (isUSRegion && staticPlan?.us_specs) {
      return {
        vcpus: staticPlan.us_specs.cpu,
        ram: staticPlan.us_specs.ram,
        disk: staticPlan.us_specs.disk,
        description: `${staticPlan.us_specs.cpu} vCPU, ${staticPlan.us_specs.ram}GB RAM, ${staticPlan.us_specs.disk}GB SSD`,
      };
    }
    
    return {
      vcpus: plan.vcpus,
      ram: plan.ram,
      disk: plan.disk,
      description: plan.description,
    };
  };

  // Get price for a plan in the selected region
  const getPlanPrice = (planId: string) => {
    if (!serverData?.pricing || !selectedRegion) return null;
    const regionPricing = serverData.pricing[selectedRegion];
    if (!regionPricing) return null;
    return regionPricing[planId] ?? null;
  };

  // Calculate total monthly cost
  const calculateTotalCost = () => {
    let total = 0;
    
    // Plan cost
    if (selectedPlan === 'pro_plan') {
      total += 49;
    }
    
    // Server cost (if managed)
    if (selectedPlan === 'starter_plan' || (selectedPlan === 'pro_plan' && serverChoice === 'managed')) {
      const price = getPlanPrice(selectedServerPlan);
      if (price !== null) {
        total += price;
      }
    }
    
    // Add-ons
    if (developerBranches) {
      total += 29;
    }
    
    return total;
  };

  // Get region display name
  const getRegionDisplayName = (regionId: string) => {
    const region = REGIONS.find(r => r.id === regionId);
    return region ? `${region.name}, ${region.location}` : regionId;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-1">Project Details</h4>
              <p className="text-sm text-muted-foreground">Set up your new Odoo project</p>
            </div>

            <Alert>
              <AlertDescription className="flex items-center gap-2">
                <Github className="h-4 w-4" />
                <span>
                  <strong>Need a new repository?</strong>{' '}
                  <a
                    href="https://github.com/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Create one on GitHub
                  </a>
                  , then refresh this page to see it in the list.
                </span>
              </AlertDescription>
            </Alert>


            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My Awesome Project"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="github-repo">GitHub Repository</Label>
                  {loadingRepos ? (
                    <div className="flex items-center space-x-2 p-3 border rounded-md">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                  ) : repos.length > 0 ? (
                    <Select value={selectedRepo} onValueChange={setSelectedRepo}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Repository" />
                      </SelectTrigger>
                      <SelectContent>
                        {repos.map((repo) => (
                          <SelectItem key={repo.id} value={repo.full_name}>
                            {repo.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-4 border border-dashed rounded-md text-center">
                      <p className="text-sm text-muted-foreground">No repositories found</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="odoo-version">Odoo Version</Label>
                  <Select value={odooVersion} onValueChange={setOdooVersion}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Version" />
                    </SelectTrigger>
                    <SelectContent>
                      {ODOO_VERSIONS.map((version) => (
                        <SelectItem key={version.version} value={version.version}>
                          {version.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="odoo-enterprise-code">
                Odoo Enterprise Subscription Code <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="odoo-enterprise-code"
                  type="password"
                  value={odooEnterpriseCode}
                  onChange={(e) => {
                    setOdooEnterpriseCode(e.target.value);
                    if (enterpriseCodeValid !== null) {
                      setEnterpriseCodeValid(null);
                      setEnterpriseValidationError(null);
                    }
                  }}
                  placeholder="Enter your Odoo Enterprise subscription code"
                  className={`flex-1 ${
                    enterpriseCodeValid === true
                      ? 'border-green-500'
                      : enterpriseCodeValid === false
                      ? 'border-red-500'
                      : ''
                  }`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={validateEnterpriseCode}
                  disabled={!odooEnterpriseCode.trim() || validatingEnterprise}
                >
                  {validatingEnterprise ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Validate'}
                </Button>
              </div>
              {enterpriseCodeValid === true && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" /> Valid subscription code
                </p>
              )}
              {enterpriseCodeValid === false && enterpriseValidationError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <X className="h-4 w-4" /> {enterpriseValidationError}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                You can add this later in project settings.
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-1">Choose Your Plan</h4>
              <p className="text-sm text-muted-foreground">Select the best plan for your needs</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card
                className={`cursor-pointer transition-all ${selectedPlan === 'pro_plan' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                onClick={() => setSelectedPlan('pro_plan')}
              >
                <CardContent className="p-6 text-center">
                  <Server className="h-14 w-14 mx-auto mb-4 text-blue-600" />
                  <h4 className="text-xl font-semibold mb-1">Pro Plan</h4>
                  <div className="text-2xl font-bold text-green-600 mb-2">$49/mo</div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Maximum flexibility - bring your own server or purchase managed hosting
                  </p>
                  <div className="text-left space-y-1">
                    <p className="text-sm text-green-600">✓ Bring Your Own Server option</p>
                    <p className="text-sm text-green-600">✓ Managed hosting available</p>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer transition-all ${selectedPlan === 'starter_plan' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                onClick={() => setSelectedPlan('starter_plan')}
              >
                <CardContent className="p-6 text-center">
                  <Database className="h-14 w-14 mx-auto mb-4 text-green-600" />
                  <h4 className="text-xl font-semibold mb-1">Starter Plan</h4>
                  <div className="text-2xl font-bold text-green-600 mb-2">from $40/mo</div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Managed hosting included - everything you need to get started quickly
                  </p>
                  <div className="text-left space-y-1">
                    <p className="text-sm text-green-600">✓ Managed hosting included</p>
                    <p className="text-sm text-green-600">✓ No server management needed</p>
                    <p className="text-sm text-green-600">✓ Perfect for getting started</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-1">Server Configuration</h4>
              <p className="text-sm text-muted-foreground">
                {selectedPlan === 'pro_plan' ? 'Choose your server option' : 'Configure your managed server'}
              </p>
            </div>

            {/* Pro Plan: Server Choice */}
            {selectedPlan === 'pro_plan' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card
                  className={`cursor-pointer transition-all ${serverChoice === 'managed' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                  onClick={() => setServerChoice('managed')}
                >
                  <CardContent className="p-4 text-center">
                    <Server className="h-12 w-12 mx-auto mb-2 text-blue-600" />
                    <h5 className="font-semibold mb-1">Managed Server</h5>
                    <p className="text-sm text-muted-foreground">Get a doploy managed server included with your project</p>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all ${serverChoice === 'byos' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                  onClick={() => setServerChoice('byos')}
                >
                  <CardContent className="p-4 text-center">
                    <Database className="h-12 w-12 mx-auto mb-2 text-gray-600" />
                    <h5 className="font-semibold mb-1">Bring Your Own Server</h5>
                    <p className="text-sm text-muted-foreground">Connect your existing server via SSH after project setup</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* BYOS Info Alert */}
            {selectedPlan === 'pro_plan' && serverChoice === 'byos' && (
              <Alert className="bg-blue-50">
                <AlertDescription>
                  <strong>Connect Your Server Later</strong><br />
                  Your project will be created and ready. You can connect your own server via SSH from your dashboard at any time.
                </AlertDescription>
              </Alert>
            )}

            {/* Server Specs - Show for Starter Plan or Pro Plan with Managed Server */}
            {(selectedPlan === 'starter_plan' || (selectedPlan === 'pro_plan' && serverChoice === 'managed')) && (
              <>
                {/* CPU Type Selection - Only for Pro Plan */}
                {selectedPlan === 'pro_plan' && (
                  <div className="mb-6">
                    <h6 className="font-semibold mb-3">CPU Type</h6>
                    <div className="grid grid-cols-2 gap-4">
                      <Card
                        className={`cursor-pointer transition-all ${cpuType === 'shared' ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'}`}
                        onClick={() => setCpuType('shared')}
                      >
                        <CardContent className="p-4">
                          <h6 className="font-semibold mb-1">Shared CPU</h6>
                          <p className="text-sm text-muted-foreground">Great performance at an affordable price</p>
                        </CardContent>
                      </Card>
                      <Card
                        className={`cursor-pointer transition-all ${cpuType === 'dedicated' ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'}`}
                        onClick={() => setCpuType('dedicated')}
                      >
                        <CardContent className="p-4">
                          <h6 className="font-semibold mb-1">Dedicated CPU</h6>
                          <p className="text-sm text-muted-foreground">Guaranteed performance for production</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Region Selection */}
                <div className="mb-6">
                  <h6 className="font-semibold mb-3">Region</h6>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {REGIONS.map((region) => (
                      <Card
                        key={region.id}
                        className={`cursor-pointer transition-all ${selectedRegion === region.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                        onClick={() => setSelectedRegion(region.id)}
                      >
                        <CardContent className="p-3 text-center">
                          <div className="text-2xl mb-1">{region.flag}</div>
                          <div className="font-semibold text-sm">{region.name}</div>
                          <div className="text-xs text-muted-foreground">{region.location}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Server Plan Selection */}
                {selectedRegion && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="font-semibold">Server Plan</h6>
                      {checkingAvailability && (
                        <span className="text-sm text-blue-600 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Checking availability...
                        </span>
                      )}
                    </div>
                    {loadingServerData ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {getAvailablePlans().map((plan) => {
                          const price = getPlanPrice(plan.id);
                          const specs = getPlanSpecs(plan.id, plan);
                          const staticDetails = getStaticPlanDetails(plan.id);
                          const isAvailable = planAvailability[plan.id];
                          const isLoading = planAvailability[plan.id] === null && checkingAvailability;
                          const isUnavailable = planAvailability[plan.id] === false;
                          const isSelected = selectedServerPlan === plan.id;
                          
                          // Card is not selectable until availability is confirmed (isAvailable === true)
                          const isSelectable = isAvailable === true;
                          
                          return (
                            <Card
                              key={plan.id}
                              className={`transition-all ${
                                isUnavailable 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : !isSelectable
                                    ? 'opacity-60 cursor-not-allowed'
                                  : isSelected 
                                    ? 'ring-2 ring-blue-500 bg-blue-50 cursor-pointer' 
                                    : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                              onClick={() => isSelectable && setSelectedServerPlan(plan.id)}
                            >
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="font-semibold mb-1">
                                      {specs.vcpus} vCPU, {specs.ram}GB RAM, {specs.disk}GB SSD
                                    </p>
                                    <p className="text-sm text-muted-foreground mb-2">
                                      <strong>{staticDetails.users || plan.capacity?.visitors}</strong>
                                      {staticDetails.workers && ` • ${staticDetails.workers}`}
                                      {plan.capacity?.workers && !staticDetails.workers && ` • ${plan.capacity.workers}`}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {staticDetails.badges?.map((badge, idx) => (
                                        <Badge 
                                          key={idx} 
                                          variant="secondary" 
                                          className={badge.toLowerCase().includes('dedicated') ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}
                                        >
                                          {badge}
                                        </Badge>
                                      ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {staticDetails.best_for || plan.description}
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <div className="font-bold text-lg">
                                      {price !== null ? `$${price.toFixed(2)}/mo` : '...'}
                                    </div>
                                    {/* Availability indicator */}
                                    <div className="mt-2">
                                      {isLoading && (
                                        <span className="text-xs text-blue-600 flex items-center justify-end gap-1 animate-pulse">
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                          Checking...
                                        </span>
                                      )}
                                      {isAvailable === true && (
                                        <span className="text-xs text-green-600 flex items-center justify-end gap-1">
                                          <Check className="h-4 w-4" /> Available
                                        </span>
                                      )}
                                      {isUnavailable && (
                                        <span className="text-xs text-red-600 flex items-center justify-end gap-1">
                                          <AlertTriangle className="h-4 w-4" /> Unavailable
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-1">Recommended Add-ons</h4>
              <p className="text-sm text-muted-foreground">Enhance your project with optional services</p>
            </div>

            <div className="space-y-4">
              {/* Development Server Add-on */}
              <Card
                className={`cursor-pointer transition-all ${developerBranches ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                onClick={() => setDeveloperBranches(!developerBranches)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <GitBranch className="h-8 w-8 text-blue-600 mt-1" />
                      <div>
                        <h5 className="font-semibold mb-1">Development Server (CI/CD)</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Automatically build and test every branch on doploy managed servers. Catch bugs before production - no server needed from you!
                        </p>
                        <div className="flex gap-2">
                          <Badge variant="secondary">Automated Testing</Badge>
                          <Badge variant="outline">Zero DevOps</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600">$29/month</div>
                      <p className="text-xs text-muted-foreground">Essential for teams</p>
                      <input
                        type="checkbox"
                        checked={developerBranches}
                        onChange={() => setDeveloperBranches(!developerBranches)}
                        className="mt-2 h-5 w-5"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Backups - Always included */}
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <Database className="h-8 w-8 text-green-600 mt-1" />
                      <div>
                        <h5 className="font-semibold mb-1">
                          Backups
                          <Badge className="ml-2 bg-green-600">✓ INCLUDED</Badge>
                        </h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Geo-replicated, immutable, point-in-time restore. Enterprise-grade data protection with instant recovery capabilities.
                        </p>
                        <p className="text-sm text-green-700 font-semibold mb-2">
                          Automatically included with all plans - pay only for what you use
                        </p>
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="bg-green-100">Geo-Replicated</Badge>
                          <Badge variant="secondary" className="bg-orange-100">Immutable</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-green-600">$0.02/GB/mo</div>
                      <p className="text-xs text-muted-foreground">Pay only for what you use</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              You can add or remove optional services anytime from your project dashboard. Backup storage is required for all projects.
            </p>
          </div>
        );

      case 5:
        const selectedPlanData = serverData && selectedServerPlan ? 
          (selectedPlan === 'starter_plan' ? serverData.starter_plans[selectedServerPlan] : 
           cpuType === 'shared' ? serverData.shared_plans[selectedServerPlan] : 
           serverData.dedicated_plans[selectedServerPlan]) : null;
        const selectedSpecs = selectedPlanData ? getPlanSpecs(selectedServerPlan, selectedPlanData) : null;
        
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-1">Review & Create</h4>
              <p className="text-sm text-muted-foreground">Review your configuration and proceed to billing</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Project Configuration */}
              <div className="md:col-span-2">
                <Card>
                  <CardContent className="p-4">
                    <h5 className="font-semibold mb-4 flex items-center gap-2">
                      <Server className="h-5 w-5 text-blue-600" />
                      Project Configuration
                    </h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Project Name:</span>
                        <p className="font-semibold">{projectName}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Odoo Version:</span>
                        <p className="font-semibold">{odooVersion}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Repository:</span>
                        <p className="font-semibold">{selectedRepo}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Plan:</span>
                        <p className="font-semibold">{selectedPlan === 'pro_plan' ? 'Pro Plan' : 'Starter Plan'}</p>
                      </div>
                      {(selectedPlan === 'starter_plan' || serverChoice === 'managed') && selectedRegion && (
                        <>
                          <div>
                            <span className="text-muted-foreground">Region:</span>
                            <p className="font-semibold">{getRegionDisplayName(selectedRegion)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Server:</span>
                            <p className="font-semibold">
                              {selectedSpecs ? `${selectedSpecs.vcpus} vCPU, ${selectedSpecs.ram}GB RAM, ${selectedSpecs.disk}GB SSD` : '-'}
                            </p>
                          </div>
                        </>
                      )}
                      {selectedPlan === 'pro_plan' && serverChoice === 'byos' && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Server:</span>
                          <p className="font-semibold">Bring Your Own Server (configure after setup)</p>
                        </div>
                      )}
                      {odooEnterpriseCode && enterpriseCodeValid && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Enterprise Code:</span>
                          <p className="font-semibold text-green-600">✓ Verified</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Billing Summary */}
              <div>
                <Card className="border-green-200">
                  <CardContent className="p-4">
                    <h5 className="font-semibold mb-4 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-green-600" />
                      Billing Summary
                    </h5>
                    <div className="space-y-2 text-sm">
                      {selectedPlan === 'pro_plan' && (
                        <div className="flex justify-between">
                          <span>Pro Plan</span>
                          <span className="font-semibold">$49.00/mo</span>
                        </div>
                      )}
                      {(selectedPlan === 'starter_plan' || (selectedPlan === 'pro_plan' && serverChoice === 'managed')) && selectedServerPlan && (
                        <div className="flex justify-between">
                          <span>Server</span>
                          <span className="font-semibold">${(getPlanPrice(selectedServerPlan) || 0).toFixed(2)}/mo</span>
                        </div>
                      )}
                      {developerBranches && (
                        <div className="flex justify-between">
                          <span>Dev Server (CI/CD)</span>
                          <span className="font-semibold">$29.00/mo</span>
                        </div>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>Backups</span>
                        <span>$0.02/GB/mo</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-green-600">${calculateTotalCost().toFixed(2)}/mo</span>
                      </div>
                      <p className="text-xs text-muted-foreground">+ usage-based backup storage</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return 'Project';
      case 2: return 'Plan';
      case 3: return 'Server';
      case 4: return 'Add-ons';
      case 5: return 'Review';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            {isFirstProject ? 'Create Your First Project' : 'Create a New Project'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure your new Odoo project with plan selection, server configuration, and add-ons
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-2 mb-6">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <div key={step} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step < currentStep
                      ? 'bg-green-500 text-white'
                      : step === currentStep
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step < currentStep ? <Check className="w-4 h-4" /> : step}
                </div>
                <span className="text-xs mt-1 text-muted-foreground">{getStepTitle(step)}</span>
              </div>
              {step < totalSteps && (
                <div
                  className={`w-8 h-0.5 mx-1 ${
                    step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <X className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <div>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={creating}
            >
              Cancel
            </Button>
          </div>
          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={creating}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={creating || checkingAvailability}
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : currentStep === totalSteps ? (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Complete Purchase
                </>
              ) : (
                'Next'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
